import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { formatEventDateRange } from '@/hooks/useActiveEvent';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import TechnologyAreaPicker from '@/components/registration/TechnologyAreaPicker';
import CollaborativeProjectForm from '@/components/registration/CollaborativeProjectForm';
import AcknowledgementSection from '@/components/registration/AcknowledgementSection';
import AthenaOptOutCheckbox from '@/components/registration/AthenaOptOutCheckbox';
import MilitaryFields from '@/components/registration/MilitaryFields';
import RegistrationConfirmation from '@/components/registration/RegistrationConfirmation';
import {
  EXHIBITOR_ORG_TYPES,
  EXHIBITOR_ACKNOWLEDGEMENTS,
  makeEmptyExhibitorProject,
} from '@/lib/constants';
import type { ExhibitorProjectEntry } from '@/lib/constants';
import type { EventPublicInfo, EmailCheckResult } from '@/types';
import { Loader2, ArrowLeft, ArrowRight, AlertCircle, Plus, Trash2, Clock, CheckCircle } from 'lucide-react';
import { ALL_VALID_RANKS, ALL_SERVICE_BRANCH_VALUES } from '@/lib/military-constants';

type Step = 'contact' | 'projects' | 'acknowledgements';

const STEPS: Step[] = ['contact', 'projects', 'acknowledgements'];

const STEP_LABELS: Record<Step, string> = {
  contact: 'Organization & Contact Info',
  projects: 'Collaborative Projects',
  acknowledgements: 'Acknowledgements',
};

type FormData = {
  organization: string;
  exhibitorOrgType: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  phone: string;
  rank: string;
  branchOfService: string;
  isRetired: boolean;
  collaborationPitch: string;
  websiteUrl: string;
  technologyAreas: string[];
  workshopInterest: boolean;
  workshopDescription: string;
};

const INITIAL_FORM: FormData = {
  organization: '',
  exhibitorOrgType: '',
  firstName: '',
  lastName: '',
  role: '',
  email: '',
  phone: '',
  rank: '',
  branchOfService: '',
  isRetired: false,
  collaborationPitch: '',
  websiteUrl: '',
  technologyAreas: [],
  workshopInterest: false,
  workshopDescription: '',
};

export default function RegisterExhibitorPage() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const { user, isAuthenticated } = useAuth();

  const [event, setEvent] = useState<EventPublicInfo | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState('');
  const [step, setStep] = useState<Step>('contact');
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [projects, setProjects] = useState<ExhibitorProjectEntry[]>([makeEmptyExhibitorProject()]);
  const [acknowledgements, setAcknowledgements] = useState<string[]>([]);
  const [athenaOptOut, setAthenaOptOut] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Email check state
  const [emailCheckResult, setEmailCheckResult] = useState<EmailCheckResult | null>(null);

  // Approved user who has already hit the 3-project cap — nothing left for them to do
  const isApprovedAtCap =
    (emailCheckResult?.hasEventRegistration ?? false) &&
    !emailCheckResult?.hasPendingRegistration &&
    (emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3 &&
    !emailCheckResult?.isRejected;

  // Project slot accounting — already-submitted projects count against the 3-project cap
  const alreadySubmittedCount = emailCheckResult?.existingProjectTitles?.length ?? 0;
  const maxNewProjects = Math.max(0, 3 - alreadySubmittedCount);

  // Trim new-project forms when the email check reveals fewer remaining slots
  useEffect(() => {
    setProjects((prev) => prev.length > maxNewProjects ? prev.slice(0, maxNewProjects) : prev);
  }, [maxNewProjects]);
  const lastCheckedEmail = useRef('');

  // Sign-in modal state
  const [showSignIn, setShowSignIn] = useState(false);
  const [showMaxProjectsModal, setShowMaxProjectsModal] = useState(false);
  const [showApprovedMaxModal, setShowApprovedMaxModal] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [returningUserVerified, setReturningUserVerified] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const url = eventId ? `/events/${eventId}/public` : '/events/active/public';
      const res = await api.get<{ success: boolean; data: EventPublicInfo }>(url);
      if (res.success) {
        if (res.data.status !== 'active') {
          setEventError('Registration for this event is not currently open.');
        } else {
          setEvent(res.data);
        }
      }
    } catch {
      setEventError(
        eventId
          ? 'Event not found. Please check your registration link.'
          : 'No active event found. Please check back later.'
      );
    } finally {
      setLoadingEvent(false);
    }
  };

  const set = (field: keyof FormData, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // --- Email check on blur ---

  const handleEmailBlur = useCallback(async () => {
    const normalizedEmail = form.email.trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) return;
    if (normalizedEmail === lastCheckedEmail.current) return;
    lastCheckedEmail.current = normalizedEmail;

    try {
      const res = await api.post<{ success: boolean; data: EmailCheckResult }>(
        '/register/check-email',
        { email: normalizedEmail, eventId: event?.id },
      );
      if (res.success && res.data.exists) {
        setEmailCheckResult(res.data);
        const isCurrentUser = isAuthenticated && user?.email?.toLowerCase() === normalizedEmail;
        if (res.data.isRejected) {
          setShowRejected(true);
        } else if (res.data.hasAccount && !isCurrentUser) {
          setShowSignIn(true);
        } else if ((res.data.existingProjectTitles?.length ?? 0) >= 3) {
          if (res.data.hasPendingRegistration) {
            setShowMaxProjectsModal(true);
          } else if (res.data.hasEventRegistration) {
            setShowApprovedMaxModal(true);
          }
        }
      } else {
        setEmailCheckResult(null);
      }
    } catch {
      // Silent fail — pre-fill is a convenience, not critical
    }
  }, [form.email, event?.id, isAuthenticated, user?.email]);

  const handleSignIn = async () => {
    const normalizedEmail = form.email.trim().toLowerCase();
    if (!signInPassword) {
      setSignInError('Password is required');
      return;
    }
    setSigningIn(true);
    setSignInError('');
    try {
      const res = await api.post<{
        user: Record<string, unknown>;
        accessToken: string;
        expiresIn: number;
      }>('/auth/registration-login', { email: normalizedEmail, password: signInPassword });
      api.setAuthTokens({ accessToken: res.accessToken, expiresIn: res.expiresIn });
      setShowSignIn(false);
      setSignInPassword('');
      setReturningUserVerified(true);
      // Pre-fill form from the returned user profile
      const p = res.user;
      const meta = (p.registrationMeta as Record<string, unknown>) ?? {};
      const safeRank = typeof p.rank === 'string' && ALL_VALID_RANKS.includes(p.rank) ? p.rank : '';
      const safeBranch = typeof p.branchOfService === 'string' && ALL_SERVICE_BRANCH_VALUES.includes(p.branchOfService) ? p.branchOfService : '';
      setForm((prev) => ({
        ...prev,
        firstName: (p.firstName as string) || prev.firstName,
        lastName: (p.lastName as string) || prev.lastName,
        role: (p.role as string) || prev.role,
        organization: (p.organization as string) || prev.organization,
        phone: (p.phone as string) || prev.phone,
        rank: safeRank || prev.rank,
        branchOfService: safeBranch || prev.branchOfService,
        isRetired: (p.isRetired as boolean) ?? prev.isRetired,
        technologyAreas: (p.accelerationInterests as string[])?.length ? (p.accelerationInterests as string[]) : prev.technologyAreas,
        collaborationPitch: (meta.collaborationPitch as string) || prev.collaborationPitch,
        exhibitorOrgType: (meta.exhibitorOrgType as string) || prev.exhibitorOrgType,
      }));
      if ((emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3 && !emailCheckResult?.isRejected) {
        if (emailCheckResult?.hasPendingRegistration) {
          setShowMaxProjectsModal(true);
        } else if (emailCheckResult?.hasEventRegistration) {
          setShowApprovedMaxModal(true);
        }
      }
    } catch {
      setSignInError('Invalid email or password. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const updateProject = (index: number, updated: ExhibitorProjectEntry) => {
    setProjects((prev) => prev.map((p, i) => (i === index ? updated : p)));
    // Clear any project errors for this index
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (key.startsWith(`project${index}.`)) {
          delete next[key];
        }
      }
      return next;
    });
  };

  const addProject = () => {
    if (projects.length >= maxNewProjects) return;
    setProjects((prev) => [...prev, makeEmptyExhibitorProject()]);
  };

  const removeProject = (index: number) => {
    if (projects.length <= 1) return;
    setProjects((prev) => prev.filter((_, i) => i !== index));
    // Re-index error keys
    setErrors((prev) => {
      const next: Record<string, string> = {};
      for (const [key, val] of Object.entries(prev)) {
        const match = key.match(/^project(\d+)\.(.+)$/);
        if (!match) {
          next[key] = val;
          continue;
        }
        const idx = parseInt(match[1]!, 10);
        if (idx === index) continue;
        const newIdx = idx > index ? idx - 1 : idx;
        next[`project${newIdx}.${match[2]!}`] = val;
      }
      return next;
    });
  };

  // --- Validation ---

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validateContact = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.organization.trim()) errs.organization = 'Organization name is required.';
    if (!form.exhibitorOrgType) errs.exhibitorOrgType = 'Organization type is required.';
    if (!form.firstName.trim()) errs.firstName = 'First name is required.';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required.';
    if (!form.role.trim()) errs.role = 'Job position is required.';
    if (!form.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!isValidEmail(form.email)) {
      errs.email = 'Please enter a valid email address.';
    }
    if (!form.collaborationPitch.trim()) {
      errs.collaborationPitch = 'Collaboration pitch is required.';
    } else if (form.collaborationPitch.length > 280) {
      errs.collaborationPitch = 'Collaboration pitch must be 280 characters or less.';
    }
    if (form.websiteUrl.trim() && !isValidUrl(form.websiteUrl.trim())) {
      errs.websiteUrl = 'Please enter a valid URL (including https://).';
    }
    if (form.technologyAreas.length === 0) {
      errs.technologyAreas = 'Select at least one technology area.';
    }
    if (form.workshopInterest && !form.workshopDescription.trim()) {
      errs.workshopDescription = 'Workshop description is required when workshop interest is selected.';
    }
    if (form.workshopInterest && form.workshopDescription.length > 2000) {
      errs.workshopDescription = 'Workshop description must be 2000 characters or less.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateProjects = (): boolean => {
    const errs: Record<string, string> = {};

    projects.forEach((project, i) => {
      const prefix = `project${i}`;
      if (!project.title.trim()) errs[`${prefix}.title`] = 'Project title is required.';
      if (!project.description.trim()) errs[`${prefix}.description`] = 'Project description is required.';
      if (project.seekingCollaboration.length === 0) {
        errs[`${prefix}.seekingCollaboration`] = 'Select at least one collaboration type.';
      }
      if (!project.classification) errs[`${prefix}.classification`] = 'Classification is required.';
      if (!project.stage) errs[`${prefix}.stage`] = 'Development stage is required.';
      if (!project.fundingStatus) errs[`${prefix}.fundingStatus`] = 'Funding status is required.';
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateAcknowledgements = (): boolean => {
    const errs: Record<string, string> = {};

    if (acknowledgements.length < EXHIBITOR_ACKNOWLEDGEMENTS.length) {
      errs.acknowledgements = 'All acknowledgements must be accepted.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validators: Record<Step, () => boolean> = {
    contact: validateContact,
    projects: validateProjects,
    acknowledgements: validateAcknowledgements,
  };

  // --- Navigation ---

  const stepIndex = STEPS.indexOf(step);

  const goNext = () => {
    if (step === 'contact') {
      const isAlreadyAuthenticated = isAuthenticated && user?.email?.toLowerCase() === form.email.trim().toLowerCase();
      if (emailCheckResult?.hasAccount && !returningUserVerified && !isAlreadyAuthenticated) {
        setShowSignIn(true);
        return;
      }
      if (emailCheckResult?.isRejected) {
        setShowRejected(true);
        return;
      }
      if (isApprovedAtCap) {
        setShowApprovedMaxModal(true);
        return;
      }
      if (emailCheckResult?.hasPendingRegistration && (emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3) {
        setShowMaxProjectsModal(true);
        return;
      }
    }
    if (!validators[step]()) return;
    if (stepIndex < STEPS.length - 1) {
      setStep(STEPS[stepIndex + 1]!);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setErrors({});
      setStep(STEPS[stepIndex - 1]!);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // --- Submission ---

  const mapOrgType = (exhibitorOrgType: string): 'industry' | 'government' => {
    return exhibitorOrgType === 'Industry' ? 'industry' : 'government';
  };

  const handleSubmit = async () => {
    const isAlreadyAuthenticated = isAuthenticated && user?.email?.toLowerCase() === form.email.trim().toLowerCase();
    if (emailCheckResult?.hasAccount && !returningUserVerified && !isAlreadyAuthenticated) {
      setErrors({ submit: 'Please sign in to submit as a returning attendee.' });
      setStep('contact');
      setShowSignIn(true);
      return;
    }
    // Validate all steps
    if (!validateContact()) {
      setStep('contact');
      return;
    }
    if (!validateProjects()) {
      setStep('projects');
      return;
    }
    if (!validateAcknowledgements()) return;

    setSubmitting(true);
    try {
      const body = {
        registrationType: 'exhibitor' as const,
        eventId: event?.id,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        rank: form.rank || undefined,
        branchOfService: form.branchOfService || undefined,
        isRetired: form.isRetired,
        organization: form.organization.trim(),
        organizationType: mapOrgType(form.exhibitorOrgType),
        exhibitorOrgType: form.exhibitorOrgType,
        role: form.role.trim(),
        bio: form.collaborationPitch.trim(),
        websiteUrl: form.websiteUrl.trim() || undefined,
        accelerationInterests: form.technologyAreas,
        workshopInterest: form.workshopInterest,
        workshopDescription: form.workshopInterest ? form.workshopDescription.trim() : undefined,
        athenaOptOut,
        acknowledgements: acknowledgements,
        projectData: projects.map((p) => ({
          title: p.title.trim(),
          description: p.description.trim(),
          seekingCollaboration: p.seekingCollaboration,
          isCrada: p.isCrada,
          classification: p.classification,
          stage: p.stage,
          fundingStatus: p.fundingStatus,
          dodImpact: p.dodImpact.trim() || null,
        })),
      };

      await api.post('/register', body);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const msg = error?.response?.data?.error || 'Failed to submit application. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render states ---

  if (loadingEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>Registration Unavailable</CardTitle>
            <CardDescription>{eventError}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-gradient-navy text-white py-8 px-4">
          <div className="max-w-2xl mx-auto flex items-center gap-5">
            <img src="/nps-logo.png" alt="NPS Foundation" className="h-14 w-auto shrink-0" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Converge @ NPS Exhibitor Application</h1>
          </div>
        </div>
        <RegistrationConfirmation registrationType="exhibitor" />
      </div>
    );
  }

  const stepNumber = stepIndex + 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-navy text-white py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-5">
          <img src="/nps-logo.png" alt="NPS Foundation" className="h-14 w-auto shrink-0" />
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3xl font-bold">Converge @ NPS</h1>
            <p className="text-sm text-blue-200">
              {event ? formatEventDateRange(event.dateStart, event.dateEnd) : ''} &middot; Exhibitor Application
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Step indicator */}
        <div className="flex justify-center gap-2 mb-2">
          {STEPS.map((_, n) => (
            <div
              key={n}
              className={`h-1.5 rounded-full transition-all ${
                n < stepNumber ? 'bg-primary w-10' : n === stepIndex ? 'bg-primary/60 w-10' : 'bg-muted w-8'
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center mb-6">
          Step {stepNumber} of {STEPS.length}: {STEP_LABELS[step]}
        </p>
        {/* Submit error */}
        {errors.submit && (
          <div className="flex gap-2 items-start p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive mb-6">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>{errors.submit}</p>
          </div>
        )}

        {/* Step 1: Organization & Contact Info */}
        {step === 'contact' && (
          <div className="space-y-6">
            {/* POC Email — first field so returning users get immediate feedback */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">POC Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => {
                  set('email', e.target.value);
                  if (returningUserVerified) setReturningUserVerified(false);
                  lastCheckedEmail.current = '';
                }}
                onBlur={handleEmailBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="contact@organization.com"
                className="h-10"
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              {emailCheckResult?.exists && emailCheckResult.hasPendingRegistration && !showSignIn && (emailCheckResult.existingProjectTitles?.length ?? 0) < 3 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                  You already have a pre-registration for this event. Your status is pending admin approval. You can still submit an additional project — it will be added to your existing pre-registration.
                </div>
              )}
              {emailCheckResult?.exists && emailCheckResult.hasEventRegistration && !emailCheckResult.hasPendingRegistration && !emailCheckResult.isRejected && (emailCheckResult.existingProjectTitles?.length ?? 0) < 3 && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-600 dark:bg-blue-950 dark:text-blue-200">
                  You already have a registration for this event. You can still submit an additional project — it will be added to your existing registration.
                </div>
              )}
              {emailCheckResult?.exists && !emailCheckResult.hasEventRegistration && emailCheckResult.hasAccount && isAuthenticated && user?.email?.toLowerCase() === form.email.trim().toLowerCase() && (
                <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800 dark:border-green-600 dark:bg-green-950 dark:text-green-200">
                  Signed in — your details have been pre-filled below. Please review and update as needed.
                </div>
              )}
            </div>

            {/* Organization Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Organization Name *</label>
              <Input
                value={form.organization}
                onChange={(e) => set('organization', e.target.value)}
                placeholder="Your organization name"
                className="h-10"
              />
              {errors.organization && <p className="text-xs text-destructive">{errors.organization}</p>}
            </div>

            {/* Organization Type */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Organization Type *</label>
              <Select value={form.exhibitorOrgType} onValueChange={(v) => set('exhibitorOrgType', v)}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select organization type" />
                </SelectTrigger>
                <SelectContent>
                  {EXHIBITOR_ORG_TYPES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.exhibitorOrgType && <p className="text-xs text-destructive">{errors.exhibitorOrgType}</p>}
            </div>

            {/* POC Name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">POC First Name *</label>
                <Input
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  placeholder="First name"
                  className="h-10"
                />
                {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">POC Last Name *</label>
                <Input
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  placeholder="Last name"
                  className="h-10"
                />
                {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
              </div>
            </div>

            {/* POC Job Position */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">POC Job Position *</label>
              <Input
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                placeholder="Job title or position"
                className="h-10"
              />
              {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
            </div>

            {/* POC Phone */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">POC Phone Number</label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="h-10"
              />
            </div>

            {/* POC Military / Service Information */}
            <MilitaryFields
              branchOfService={form.branchOfService}
              rank={form.rank}
              isRetired={form.isRetired}
              onBranchChange={(v) => { set('branchOfService', v); set('rank', ''); }}
              onRankChange={(v) => set('rank', v)}
              onRetiredChange={(v) => set('isRetired', v)}
            />

            {/* Collaboration Pitch */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Organization Collaboration Pitch *</label>
              <p className="text-xs text-muted-foreground">
                Briefly describe your organization and what you hope to collaborate on at Converge.
              </p>
              <Textarea
                value={form.collaborationPitch}
                onChange={(e) => set('collaborationPitch', e.target.value)}
                maxLength={280}
                rows={3}
                placeholder="What does your organization do and how would you like to collaborate with NPS?"
              />
              <p className="text-xs text-muted-foreground text-right">{form.collaborationPitch.length}/280</p>
              {errors.collaborationPitch && <p className="text-xs text-destructive">{errors.collaborationPitch}</p>}
            </div>

            {/* Organization Website */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Organization Website</label>
              <Input
                type="url"
                value={form.websiteUrl}
                onChange={(e) => set('websiteUrl', e.target.value)}
                placeholder="https://example.com"
                maxLength={200}
                className="h-10"
              />
              {errors.websiteUrl && <p className="text-xs text-destructive">{errors.websiteUrl}</p>}
            </div>

            {/* Technology Areas */}
            <TechnologyAreaPicker
              selected={form.technologyAreas}
              onChange={(values) => set('technologyAreas', values)}
              max={5}
              error={errors.technologyAreas}
            />

            {/* Workshop Interest */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Workshop Interest</label>
              <p className="text-sm text-muted-foreground">
                Technology &amp; Tools: workshops led by industry serve as both an introduction and a networking session for participants to explore how a tech, tool or capability produced by your company could be applied to their problems.{' '}
                <strong>Submission request does not guarantee you will host a workshop.</strong>
              </p>
              <label className="flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 border-border hover:border-primary/50">
                <Checkbox
                  checked={form.workshopInterest}
                  onCheckedChange={(checked) => {
                    set('workshopInterest', !!checked);
                    if (!checked) set('workshopDescription', '');
                  }}
                  className="h-4 w-4"
                />
                <span className="text-sm">My organization is interested in leading or co-leading a workshop.</span>
              </label>

              {form.workshopInterest && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Workshop Description *</label>
                  <Textarea
                    value={form.workshopDescription}
                    onChange={(e) => set('workshopDescription', e.target.value)}
                    maxLength={2000}
                    rows={4}
                    placeholder="Describe the workshop topic, format, and intended audience."
                  />
                  <p className="text-xs text-muted-foreground text-right">{form.workshopDescription.length}/2000</p>
                  {errors.workshopDescription && <p className="text-xs text-destructive">{errors.workshopDescription}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Collaborative Projects */}
        {step === 'projects' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Collaborative Projects</h2>
              <p className="text-xs text-muted-foreground">
                Add 1 to 3 collaborative projects your organization would like to showcase or explore at Converge.
              </p>
            </div>

            {emailCheckResult?.existingProjectTitles && emailCheckResult.existingProjectTitles.length > 0 && (
              <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-600 dark:bg-blue-950 dark:text-blue-200">
                <p className="font-medium mb-1">Previously submitted {emailCheckResult.existingProjectTitles.length === 1 ? 'project' : 'projects'}:</p>
                <ul className="list-disc list-inside">
                  {emailCheckResult.existingProjectTitles.map((title, i) => (
                    <li key={i}>{title}</li>
                  ))}
                </ul>
                <p className="mt-1.5 text-blue-700 dark:text-blue-300">Submit a new project below — it will be added to your existing registration.</p>
              </div>
            )}

            {maxNewProjects === 0 ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                You have already submitted the maximum of 3 projects for this event. No additional projects can be added.
              </div>
            ) : (
              <>
                {projects.map((project, index) => (
                  <div key={index} className="relative">
                    <CollaborativeProjectForm
                      variant="exhibitor"
                      project={project}
                      onChange={(updated) => updateProject(index, updated as ExhibitorProjectEntry)}
                      index={index + alreadySubmittedCount}
                      errors={errors}
                    />
                    {projects.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-3 right-3 text-destructive hover:text-destructive/80"
                        onClick={() => removeProject(index)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                {projects.length < maxNewProjects && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addProject}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Project ({projects.length + alreadySubmittedCount}/3)
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 3: Acknowledgements */}
        {step === 'acknowledgements' && (
          <div className="space-y-6">
            <AcknowledgementSection
              acknowledgements={EXHIBITOR_ACKNOWLEDGEMENTS}
              accepted={acknowledgements}
              onChange={setAcknowledgements}
              error={errors.acknowledgements}
            />
            <AthenaOptOutCheckbox
              checked={athenaOptOut}
              onChange={setAthenaOptOut}
            />
          </div>
        )}

        {/* Validation banner */}
        {Object.entries(errors).filter(([k, v]) => k !== 'submit' && v).length > 0 && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            Please fix the highlighted fields above before continuing.
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-8">
          {stepIndex > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              className="flex-1 h-11 md:h-10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}

          {emailCheckResult?.isRejected ? (
            <div className="flex-1 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-center text-destructive">
              Your registration was not approved. Check your email for a resubmit link.
            </div>
          ) : isApprovedAtCap && step === 'contact' ? (
            <div className="flex-1 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-center text-green-800 dark:border-green-600 dark:bg-green-950 dark:text-green-200">
              You have been approved and have submitted the maximum number of projects.
            </div>
          ) : emailCheckResult?.hasPendingRegistration && (emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3 ? (
            <div className="flex-1 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-center text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              Your pre-registration is pending admin approval.
            </div>
          ) : stepIndex < STEPS.length - 1 ? (
            <Button onClick={goNext} className="flex-1 h-11 md:h-10">
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} className="flex-1 h-11 md:h-10" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Exhibitor Application'
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Sign-in modal for existing accounts */}
      <Dialog open={showSignIn} onOpenChange={(open) => { if (!signingIn) setShowSignIn(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign in to continue</DialogTitle>
            <DialogDescription>
              An account with <strong>{form.email}</strong> exists from previous events. Please sign in here, then continue completing the registration form (double-check all pre-populated fields).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Email</Label>
              <Input value={form.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                value={signInPassword}
                onChange={(e) => setSignInPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSignIn(); }}
                placeholder="Enter your password"
                autoFocus
              />
            </div>
            {signInError && (
              <p className="text-sm text-destructive">{signInError}</p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={handleSignIn} disabled={signingIn} className="w-full">
                {signingIn ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Signing in...</> : 'Sign In'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Forgot your password?{' '}
                <Link to="/forgot-password?redirect=/register/exhibitor" className="text-primary hover:underline">
                  Click here to reset it
                </Link>
                . Then return to the registration page to continue registering for the event. You must submit the registration form in order to attend the current event.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approved-max modal — approved attendee has already hit the 3-project cap */}
      <Dialog open={showApprovedMaxModal} onOpenChange={setShowApprovedMaxModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center dark:bg-green-900">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <DialogTitle className="text-center">Pre-Registration Approved</DialogTitle>
            <DialogDescription className="text-center">
              You have been approved for this event and have already submitted the maximum number of projects (3). There is nothing left to do on this page.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowApprovedMaxModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Max projects modal — returning attendee already has 3 projects pending approval */}
      <Dialog open={showMaxProjectsModal} onOpenChange={setShowMaxProjectsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center dark:bg-amber-900">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-center">Pre-Registration Pending Admin Approval</DialogTitle>
            <DialogDescription className="text-center">
              You already have submitted a pre-registration for this event. It is pending admin approval. You have also submitted the maximum number of projects (3). There is nothing left to do on this page.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowMaxProjectsModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejected modal — registration was not approved */}
      <Dialog open={showRejected} onOpenChange={setShowRejected}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <DialogTitle className="text-center">Registration Not Approved</DialogTitle>
            <DialogDescription className="text-center">
              Your registration for this event was not approved. Check your email for a resubmit link to try again.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowRejected(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
